import { Component, ChangeDetectionStrategy, ChangeDetectorRef,
  EventEmitter, OnInit, Output, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  gridPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { CfcSplitOnOwnerPercentEditService } from './cfc-split-on-owner-percent-edit.service';

@Component({
    selector: 'gtw-cfc-split-on-owner-percent-edit',
    templateUrl: './cfc-split-on-owner-percent-edit.component.html',
    styleUrls: ['./cfc-split-on-owner-percent-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CfcSplitOnOwnerPercentEditComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private isInitialized = false;

  GENERAL_CONFIG: any = {};
  private globalParams: any = {};
  filterParams: any = {};
  USER_SETTINGS: any = {};

  minimise = false;
  loadingData = true;
  eoyAdjmentsLoading = false;
  isSaveClicked = false;

  selectedGridRow: any[] = [];
  cfc_combination_key = 0;
  adjustmentDtls: any[] = [];
  originalEoYAdjAmt = 0;
  isSplitOnOwnership = '';
  isOwnershipChanged = '';
  splitOnOwnershipOld = '';
  is_locked: any = null;
  scenario = '';
  jcd_key = '';

  showConfirmationDialog = false;
  confirmationMessage = '';
  confirmationHeader = '';
  private pendingSaveAction: (() => void) | null = null;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private cfcSplitOnOwnerPercentEditService: CfcSplitOnOwnerPercentEditService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.gtwWorkspace.setTabActive.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    });
    this.gtwGlobal.globalParamsChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    });
    this.tryInit();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalParams = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);

    const gridData = gridPayloadFromActiveScreen(activeScreen);
    if (gridData && gridData.rowData) {
      this.selectedGridRow = [_.clone(gridData.rowData)];
      this.cfc_combination_key = this.selectedGridRow[0]?.CFC_COMBINATION_KEY || 0;
      this.originalEoYAdjAmt = (this.selectedGridRow[0]?.BOY_BASIS_AMT || 0) + (this.selectedGridRow[0]?.ADJ_BASIS_AMT || 0);
      this.isOwnershipChanged = this.selectedGridRow[0]?.IS_OWNERSHIP_CHANGED || '';
      this.splitOnOwnershipOld = this.selectedGridRow[0]?.IS_SPLIT_OWNERSHIP || '';
      this.isSplitOnOwnership = this.selectedGridRow[0]?.IS_SPLIT_OWNERSHIP || '';
    }
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    if (!this.cfc_combination_key) return;
    this.isInitialized = true;

    this.scenario = this.filterParams?.scenario || this.globalParams?.scenario || '';
    this.jcd_key = this.filterParams?.jcd_key || this.globalParams?.jcd_key || '';

    this.checkLockStatus();
    this.getEoYAdjustmentDtls();
  }

  private checkLockStatus(): void {
    this.cfcSplitOnOwnerPercentEditService.checkLockStatus(
      this.GENERAL_CONFIG.base_url,
      this.scenario,
      this.jcd_key,
      this.globalParams,
      this.filterParams,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.is_locked = data.jsonObject;
        if (this.is_locked?.[0]?.LOCK_STATUS == 'Y') {
          this.cancel();
          this.gtwSnackbarService.openSnackBar(
            `Tax Return has already been filed for tax year ${this.filterParams.tax_year}.`,
            'gtw-snackbar--red',
          );
        }
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error('Error checking lock status:', error);
      },
    });
  }

  getEoYAdjustmentDtls(): void {
    this.eoyAdjmentsLoading = true;
    this.cdr.detectChanges();

    let splitOwnerFlag = '';
    if (this.isSplitOnOwnership) {
      splitOwnerFlag = this.isSplitOnOwnership == 'Yes' ? 'Y' : 'N';
    }

    this.cfcSplitOnOwnerPercentEditService.getEoYAdjustmentDtls(
      this.GENERAL_CONFIG.base_url,
      this.cfc_combination_key,
      splitOwnerFlag,
      this.globalParams,
      this.filterParams,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: any) => {
        if (response?.jsonObject) {
          this.adjustmentDtls = response.jsonObject;
        }
        this.loadingData = false;
        this.eoyAdjmentsLoading = false;
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error('Error loading EoY adjustment details:', error);
        this.loadingData = false;
        this.eoyAdjmentsLoading = false;
        this.gtwSnackbarService.openSnackBar('Error loading EoY adjustment details', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  onSplitOwnershipChange(): void {
    this.getEoYAdjustmentDtls();
  }

  save(): void {
    if (this.splitOnOwnershipOld && this.isSplitOnOwnership &&
        this.splitOnOwnershipOld != this.isSplitOnOwnership) {
      this.confirmationMessage = `Are you sure to change Split Basis Based on Ownership from ${this.splitOnOwnershipOld} to ${this.isSplitOnOwnership} and save?`;
    } else {
      this.confirmationMessage = 'Are you sure to save?';
    }

    this.confirmationHeader = 'Split on Ownership';
    this.pendingSaveAction = () => this.performSave();
    this.showConfirmationDialog = true;
    this.cdr.detectChanges();
  }

  confirmSave(): void {
    this.showConfirmationDialog = false;
    this.pendingSaveAction?.();
    this.pendingSaveAction = null;
    this.cdr.detectChanges();
  }

  cancelConfirmation(): void {
    this.showConfirmationDialog = false;
    this.pendingSaveAction = null;
    this.isSaveClicked = false;
    this.cdr.detectChanges();
  }

  private performSave(): void {
    this.isSaveClicked = true;

    for (const row of this.adjustmentDtls) {
      this.roundEoYAmountToTwoDecimals(row);
    }

    let enteredTotal = 0;
    for (const row of this.adjustmentDtls) {
      if (!this.isSplitOnOwnership || row.AMOUNT == null) {
        this.gtwSnackbarService.openSnackBar(
          'Please select/enter all the required fields.',
          'gtw-snackbar--red',
        );
        this.isSaveClicked = false;
        return;
      }
      enteredTotal += (row.AMOUNT || 0);
    }

    if (enteredTotal.toFixed(2) != this.originalEoYAdjAmt.toFixed(2)) {
      this.gtwSnackbarService.openSnackBar(
        'Total of the entered EoY adjustment should be equal to (BoY amount and Adjustment amount)',
        'gtw-snackbar--red',
      );
      this.isSaveClicked = false;
      return;
    }

    if (this.is_locked?.[0]?.LOCK_STATUS == 'Y') {
      this.cancel();
      this.gtwSnackbarService.openSnackBar(
        'You cannot perform this action as Tax return is already filed.',
        'gtw-snackbar--red',
      );
      this.isSaveClicked = false;
      return;
    }

    this.prepAndSendDetails();
  }

  private prepAndSendDetails(): void {
    const returnClobSettingsObj: any = {
      sso_id: this.USER_SETTINGS?.user?.sso_id || '',
      object_id: this.cfc_combination_key,
    };

    const dataToSave: any[] = [];
    for (const row of this.adjustmentDtls) {
      const tempObj: any = {
        cfc_combination_key: row.CFC_COMBINATION_KEY,
        us_sh_combination_key: row.US_SH_COMBINATION_KEY,
        adj_key: row.ADJ_KEY,
        ownership_ratio: row.CURR_RATIO,
        amount: row.AMOUNT,
      };

      if (this.isSplitOnOwnership == 'Yes') {
        tempObj.operation_type = 'D';
      } else {
        tempObj.operation_type = (row.ADJ_KEY == null || row.ADJ_KEY == 0) ? 'I' : 'U';
      }
      dataToSave.push(tempObj);
    }

    this.sendDetails(returnClobSettingsObj, dataToSave, 'EoY Adjustment Amount(s) have been saved/updated');
  }

  private sendDetails(returnClobSettingsObj: any, defaultsChangeDetails: any[], message: string): void {
    this.cfcSplitOnOwnerPercentEditService.saveData(
      this.GENERAL_CONFIG.base_url,
      returnClobSettingsObj,
      defaultsChangeDetails,
      this.globalParams,
      this.filterParams,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result: any) => {
        if (result.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
          this.isSaveClicked = false;
        } else {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
          this.cancel();
        }
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this.isSaveClicked = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  getSortedAdjustmentDtls(): any[] {
    if (!this.adjustmentDtls) return [];
    return [...this.adjustmentDtls].sort((a, b) => (a.ADJ_KEY || 0) - (b.ADJ_KEY || 0));
  }

  trackByAdjKey(index: number, item: any): any {
    return item.ADJ_KEY ?? index;
  }

  roundEoYAmountToTwoDecimals(item: any): void {
    if (item.AMOUNT != null && item.AMOUNT !== '') {
      const numValue = parseFloat(item.AMOUNT.toString());
      if (!isNaN(numValue)) {
        item.AMOUNT = parseFloat(numValue.toFixed(2));
      }
    }
  }
}
